import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from './client';
import type { ChainingMethod, MasteryDirection, TargetDataType } from './programBank';

export type ClientTargetStatus = 'In Progress' | 'Mastered' | 'On Hold' | 'Discontinued';

export interface ClientProgramTarget {
    id: string;
    clientProgramId: string;
    sourceBankId: string | null;
    name: string;
    description: string;
    dataType: TargetDataType;
    trials: number | null;
    taSteps: unknown[] | null;
    chainingMethod: ChainingMethod | null;
    masteryDirection: MasteryDirection;
    masteryValue: number | null;
    masterySessions: number | null;
    status: ClientTargetStatus;
    sortOrder: number;
    generalization: string;
    baselineDate: string | null;
    baseline: string;
}

export interface ClientProgramTargetWritePayload {
    clientProgramId?: string;
    name?: string;
    description?: string;
    dataType?: TargetDataType;
    trials?: number | null;
    taSteps?: unknown[] | null;
    chainingMethod?: ChainingMethod | null;
    masteryDirection?: MasteryDirection;
    masteryValue?: number | null;
    masterySessions?: number | null;
    status?: ClientTargetStatus;
    sortOrder?: number;
    generalization?: string;
    baselineDate?: string | null;
    baseline?: string;
}

interface ListOpts {
    clientProgramId: string;
    status?: ClientTargetStatus;
    includeRetired?: boolean;
}

async function fetchClientProgramTargets(opts: ListOpts): Promise<ClientProgramTarget[]> {
    const params: Record<string, string> = { clientProgramId: opts.clientProgramId };
    if (opts.status) params.status = opts.status;
    if (opts.includeRetired) params.includeRetired = '1';
    const res = await api.get<ClientProgramTarget[]>('/client-program-targets', { params });
    return res.data;
}

export function useClientProgramTargets(opts: ListOpts) {
    return useQuery({
        queryKey: [
            'client-program-targets',
            opts.clientProgramId,
            opts.status ?? 'any',
            opts.includeRetired ? 'with-retired' : 'active',
        ] as const,
        queryFn: () => fetchClientProgramTargets(opts),
        enabled: !!opts.clientProgramId,
        staleTime: 60_000,
    });
}

async function fetchClientProgramTargetById(id: string): Promise<ClientProgramTarget> {
    const res = await api.get<ClientProgramTarget>(`/client-program-targets/${id}`);
    return res.data;
}

export function useClientProgramTarget(id: string | null | undefined) {
    return useQuery({
        queryKey: ['client-program-target', id] as const,
        queryFn: () => fetchClientProgramTargetById(id as string),
        enabled: !!id,
        staleTime: 60_000,
    });
}

export function useCreateClientProgramTarget(clientProgramId: string) {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (payload: ClientProgramTargetWritePayload) => {
            const res = await api.post<ClientProgramTarget>('/client-program-targets', {
                ...payload,
                clientProgramId,
            });
            return res.data;
        },
        onSuccess: () => qc.invalidateQueries({ queryKey: ['client-program-targets', clientProgramId] }),
    });
}

export function useUpdateClientProgramTarget(clientProgramId: string) {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async ({ id, payload }: { id: string; payload: ClientProgramTargetWritePayload }) => {
            const res = await api.patch<ClientProgramTarget>(`/client-program-targets/${id}`, payload);
            return res.data;
        },
        onSuccess: () => qc.invalidateQueries({ queryKey: ['client-program-targets', clientProgramId] }),
    });
}

export function useRetireClientProgramTarget(clientProgramId: string) {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (id: string) => {
            await api.delete(`/client-program-targets/${id}`);
        },
        onSuccess: () => qc.invalidateQueries({ queryKey: ['client-program-targets', clientProgramId] }),
    });
}

/**
 * Reorder the non-retired ({In Progress, On Hold}) targets of one client
 * program. `ids` must cover every non-retired target id for the program
 * exactly once. Server rewrites each row's sort_order to its position in
 * the array; retired targets (Mastered, Discontinued) keep their stored
 * sort_order. Optimistic update with snapshot rollback on failure.
 */
export function useReorderClientProgramTargets(clientProgramId: string) {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async (ids: string[]) => {
            const res = await api.put<ClientProgramTarget[]>(
                '/client-program-targets/reorder',
                { clientProgramId, ids },
            );
            return res.data;
        },
        onMutate: async (ids) => {
            await qc.cancelQueries({ queryKey: ['client-program-targets', clientProgramId] });
            const snapshots = qc.getQueriesData<ClientProgramTarget[]>({
                queryKey: ['client-program-targets', clientProgramId],
            });
            for (const [key, snap] of snapshots) {
                if (!snap) continue;
                const byId = new Map(snap.map((t) => [t.id, t]));
                const reordered = ids
                    .map((id, idx) => {
                        const row = byId.get(id);
                        return row ? { ...row, sortOrder: idx + 1 } : null;
                    })
                    .filter((r): r is ClientProgramTarget => r !== null);
                const untouched = snap.filter((t) => !ids.includes(t.id));
                qc.setQueryData(key, [...reordered, ...untouched]);
            }
            return { snapshots };
        },
        onError: (_err, _ids, ctx) => {
            if (!ctx) return;
            for (const [key, snap] of ctx.snapshots) {
                qc.setQueryData(key, snap);
            }
        },
        onSettled: () => {
            qc.invalidateQueries({ queryKey: ['client-program-targets', clientProgramId] });
        },
    });
}

export interface SaveTargetToBankResponse {
    bankId: string;
}

export function useSaveClientProgramTargetToBank(clientProgramId: string) {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: async ({ id, createNew }: { id: string; createNew?: boolean }) => {
            const res = await api.post<SaveTargetToBankResponse>(
                `/client-program-targets/${id}/save-to-bank`,
                createNew ? { createNew: true } : {},
            );
            return res.data;
        },
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: ['client-program-targets', clientProgramId] });
            qc.invalidateQueries({ queryKey: ['program-bank'] });
        },
    });
}
